import api from './api';

/**
 * Serviço para gerenciamento de projetos
 */
const projectsService = {
  // Listar todos os projetos
  getAllProjects: async (params = {}) => {
    try {
      const response = await api.get('/projects', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
      throw error;
    }
  },

  // Buscar projeto por ID
  getProjectById: async (id) => {
    try {
      const response = await api.get(`/projects/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar projeto:', error);
      throw error;
    }
  },

  // Aprovar projeto 3D e criar projeto
  approve3DProject: async (surveyId) => {
    try {
      const response = await api.post(`/projects/surveys/${surveyId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Erro ao aprovar projeto 3D:', error);
      throw error;
    }
  },

  // Rejeitar projeto 3D e retornar para ajustes
  reject3DProject: async (surveyId, reason) => {
    try {
      const response = await api.post(`/projects/surveys/${surveyId}/reject`, { reason });
      return response.data;
    } catch (error) {
      console.error('Erro ao rejeitar projeto 3D:', error);
      throw error;
    }
  },

  // Criar projeto a partir de levantamento aprovado pelo cliente
  createProjectFromSurvey: async (surveyId) => {
    try {
      const response = await api.post(`/projects/surveys/${surveyId}/create`);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar projeto a partir do levantamento:', error);
      throw error;
    }
  },

  // Criar projeto manual
  createManualProject: async (data) => {
    try {
      const response = await api.post('/projects/manual', data);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar projeto manual:', error);
      throw error;
    }
  },

  // Buscar itens de estoque disponíveis para venda em projeto rápido
  getQuickStockItems: async () => {
    try {
      const response = await api.get('/projects/quick-stock-items');
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar itens de estoque:', error);
      throw error;
    }
  },

  // Criar projeto rápido
  createQuickProject: async (data) => {
    try {
      const response = await api.post('/projects/quick', data);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar projeto rápido:', error);
      throw error;
    }
  },

  // Criar projeto a partir da pasta do cliente com contrato financeiro completo
  createProjectWithContract: async (data) => {
    try {
      const response = await api.post('/projects/with-contract', data);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar projeto com contrato:', error);
      throw error;
    }
  },

  // Confirmar entrada de projeto rápido no caixa da empresa
  confirmQuickProjectCashbox: async (projectId, data = {}) => {
    try {
      const response = await api.post(`/projects/${projectId}/confirm-cashbox`, data);
      return response.data;
    } catch (error) {
      console.error('Erro ao confirmar projeto rápido no caixa:', error);
      throw error;
    }
  },

  // Obter estatísticas
  getProjectStats: async (params = {}) => {
    try {
      const response = await api.get('/projects/stats', { params });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  },

  // Atualizar status do projeto (aprovar ou rejeitar orçamento)
  updateProjectStatus: async (projectId, status, notes = '') => {
    try {
      const response = await api.put(`/projects/${projectId}/status`, {
        status,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar status do projeto:', error);
      throw error;
    }
  },

  // Buscar condições de pagamento disponíveis para um projeto
  getProjectPaymentConditions: async (projectId) => {
    try {
      const response = await api.get(`/projects/${projectId}/payment-conditions`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar condições de pagamento do projeto:', error);
      throw error;
    }
  },

  // Atualizar condição de pagamento escolhida pelo cliente
  updateSelectedPaymentCondition: async (projectId, paymentConditionId) => {
    try {
      const response = await api.put(`/projects/${projectId}/selected-payment-condition`, {
        payment_condition_id: paymentConditionId
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar condição de pagamento escolhida:', error);
      throw error;
    }
  },

  // Sincronizar condições de pagamento para projeto existente
  syncProjectPaymentConditions: async (projectId) => {
    try {
      const response = await api.post(`/projects/${projectId}/sync-payment-conditions`);
      return response.data;
    } catch (error) {
      console.error('Erro ao sincronizar condições de pagamento:', error);
      throw error;
    }
  },

  // Buscar histórico de condições de pagamento de um projeto
  getPaymentConditionHistory: async (projectId) => {
    try {
      const response = await api.get(`/projects/${projectId}/payment-condition-history`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar histórico de condições de pagamento:', error);
      throw error;
    }
  },

  // ===== FORMATTERS =====

  // Formatar código do projeto
  formatProjectCode: (code) => {
    if (!code) return 'N/A';
    return code.replace('PROJ-', '').replace('-', '/');
  },

  // Formatar status
  formatStatus: (status) => {
    const statusMap = {
      'DRAFT': 'Rascunho',
      'PLANNING': 'Planejamento',
      'IN_PROGRESS': 'Em Andamento',
      'PRODUCTION': 'Produção',
      'ASSEMBLY': 'Montagem',
      'COMPLETED': 'Concluído',
      'DELIVERED': 'Entregue',
      'CANCELLED': 'Cancelado',
      'AWAITING_CLIENT': 'Aguardando Cliente',
      'APPROVED': 'Aprovado',
      'REJECTED': 'Rejeitado'
    };
    return statusMap[status] || status;
  },

  // Formatar prioridade
  formatPriority: (priority) => {
    const priorityMap = {
      'LOW': 'Baixa',
      'NORMAL': 'Normal',
      'HIGH': 'Alta',
      'URGENT': 'Urgente'
    };
    return priorityMap[priority] || priority;
  },

  // Cores para status
  getStatusColor: (status) => {
    const colorMap = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'PLANNING': 'bg-blue-100 text-blue-800',
      'IN_PROGRESS': 'bg-yellow-100 text-yellow-800',
      'PRODUCTION': 'bg-orange-100 text-orange-800',
      'ASSEMBLY': 'bg-purple-100 text-purple-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'DELIVERED': 'bg-emerald-100 text-emerald-800',
      'CANCELLED': 'bg-red-100 text-red-800',
      'AWAITING_CLIENT': 'bg-indigo-100 text-indigo-800',
      'APPROVED': 'bg-green-100 text-green-800',
      'REJECTED': 'bg-red-100 text-red-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  },

  // Cores para prioridade
  getPriorityColor: (priority) => {
    const colorMap = {
      'LOW': 'bg-gray-100 text-gray-800',
      'NORMAL': 'bg-blue-100 text-blue-800',
      'HIGH': 'bg-orange-100 text-orange-800',
      'URGENT': 'bg-red-100 text-red-800'
    };
    return colorMap[priority] || 'bg-gray-100 text-gray-800';
  },

  // Formatadores de data
  formatDate: (dateString) => {
    if (!dateString) return 'Não definido';

    // Formatar diretamente da string ISO sem criar objeto Date (evita problema de fuso horário)
    if (typeof dateString === 'string' && dateString.includes('T')) {
      const [year, month, day] = dateString.split('T')[0].split('-');
      return `${day}/${month}/${year}`;
    }

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  },

  formatDateTime: (dateString) => {
    if (!dateString) return 'Não definido';

    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  },

  // Formatar valor monetário
  formatCurrency: (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  },

  // Deletar projeto
  deleteProject: (projectId) => {
    return api.delete(`/projects/${projectId}`);
  },

  // Workflow de Execução
  updateProjectStage: (projectId, toStage, notes = '') => {
    return api.put(`/projects/${projectId}/stage`, { to_stage: toStage, notes });
  },

  getProjectStages: (projectId) => {
    return api.get(`/projects/${projectId}/stages`);
  },

  recordStageProgress: (projectId, data) => {
    return api.post(`/projects/${projectId}/stage-progress`, data);
  },

  getProjectProgress: (projectId) => {
    return api.get(`/projects/${projectId}/stage-progress`);
  },

  getProjectsByStage: (stage, params = {}) => {
    return api.get(`/projects/by-stage/${stage}`, { params });
  },

  getProjectsByClientId: (clientId, params = {}) => {
    return api.get('/projects', { params: { ...params, client_id: clientId } });
  },

  // Arquivos do Projeto
  getProjectFiles: (projectId) => {
    return api.get(`/projects/${projectId}/files`);
  },

  uploadProjectFiles: (projectId, formData) => {
    return api.post(`/projects/${projectId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  downloadProjectFile: (fileId) => {
    return api.get(`/projects/files/${fileId}/download`, {
      responseType: 'blob'
    });
  },

  deleteProjectFile: (fileId) => {
    return api.delete(`/projects/files/${fileId}`);
  },

  // Recebimentos Extras
  getProjectExtraIncomes: (projectId) => {
    return api.get(`/projects/${projectId}/extra-incomes`);
  },

  getProjectExtraIncomeById: (projectId, id) => {
    return api.get(`/projects/${projectId}/extra-incomes/${id}`);
  },

  createProjectExtraIncome: (projectId, data) => {
    return api.post(`/projects/${projectId}/extra-incomes`, data);
  },

  updateProjectExtraIncome: (projectId, id, data) => {
    return api.put(`/projects/${projectId}/extra-incomes/${id}`, data);
  },

  deleteProjectExtraIncome: (projectId, id) => {
    return api.delete(`/projects/${projectId}/extra-incomes/${id}`);
  },

  confirmProjectExtraIncomeEntry: (projectId, extraIncomeId, entryId) => {
    return api.post(`/projects/${projectId}/extra-incomes/${extraIncomeId}/confirm/${entryId}`);
  },

  // Arquivos de Recebimentos Extras
  uploadExtraIncomeFiles: (projectId, extraIncomeId, formData) => {
    return api.post(`/projects/${projectId}/extra-incomes/${extraIncomeId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getExtraIncomeFiles: (projectId, extraIncomeId) => {
    return api.get(`/projects/${projectId}/extra-incomes/${extraIncomeId}/files`);
  },

  downloadExtraIncomeFile: (fileId) => {
    return api.get(`/projects/extra-incomes/files/${fileId}/download`, { responseType: 'blob' });
  },

  deleteExtraIncomeFile: (fileId) => {
    return api.delete(`/projects/extra-incomes/files/${fileId}`);
  }
};

export default projectsService;
